<template>
  <div class="merchant-container">
    <loading :active.sync="isLoading" :can-cancel="false" :is-full-page="true"></loading>
    <div class="side-nav">
      <MerchantSideNav />
      <MenuToggle />
    </div>
    <div class="merchant-body transaction-body">
      <header>
        <MerchantHeader />
      </header>
      <div class="dashboard-content">
        <div class="row align-items-center mt-5">
          <h3>{{ title }}</h3>
          <span class="material-icons mx-3"> arrow_forward_ios </span>
          <h3>Amej Pay</h3>
        </div>

        <!-- FILTERS -->
        <div class="row align-items-center">
          <div class="mb-3 col-12 col-lg-3 col-xl-2">
            <b-form-select v-model="filters.status" :options="statusOptions"></b-form-select>
          </div>
          <div class="mb-3 col-12 col-lg-3 col-xl-2">
            <b-form-input v-model="filters.dateFrom" type="date" placeholder="From"></b-form-input>
          </div>
          <div class="mb-3 col-12 col-lg-3 col-xl-2">
            <b-form-input v-model="filters.dateTo" type="date" placeholder="To"></b-form-input>
          </div>
          <div class="mb-3 col-12 col-lg-3 col-xl-3">
            <b-form-input
              v-model="filters.search"
              type="search"
              placeholder="Search order id, email, transaction id.."
              @keyup.enter="applyFilters"
            ></b-form-input>
          </div>
          <div class="mb-3 col-12 col-lg-3 col-xl-1">
            <b-button variant="primary" @click="applyFilters">Submit</b-button>
          </div>
          <div class="mb-3 col-12 col-lg-3 col-xl-1">
            <b-button variant="light" @click="resetFilters">Reset</b-button>
          </div>
          <div class="mb-3 col-12 col-lg-3 col-xl-2">
            <a href="#" class="download-link" @click.prevent="exportList">
              <span class="material-icons">download</span>
              <span>{{ isExporting ? "Exporting..." : "Download" }}</span>
            </a>
          </div>
        </div>

        <div class="row pl-0">
          <div class="col-12 col-md-12 col-lg-10 col-xl-12">
            <div class="row m-0 mb-2" v-if="totalItems">
              <small class="text-muted">
                Showing {{ firstIndex }}-{{ lastIndex }} of {{ totalItems }} transactions
              </small>
            </div>
            <!-- Deliberately labelled with the row count. These sum the rows ON SCREEN,
                 which is NOT the same as the dashboard's Gross Revenue: that counts paid
                 payments only, while an unfiltered list also carries pending ones. On the
                 Success screen (filtered to paid) the two do reconcile. -->
            <div class="row m-0 mb-2 page-totals" v-if="rows.length">
              <small>
                <span class="pt-label">This page ({{ rows.length }} rows shown):</span>
                <span class="pt-item">Gross <strong>{{ pageTotals.gross }}</strong></span>
                <span class="pt-item">Fees <strong>{{ pageTotals.fee }}</strong></span>
                <span class="pt-item">Net <strong>{{ pageTotals.net }}</strong></span>
              </small>
            </div>

            <b-table
              hover
              responsive
              striped
              bordered
              outlined
              show-empty
              :items="rows"
              :fields="fields"
              :table-variant="tableVariants"
              :head-variant="headVariant"
            >
              <template #empty>
                <div class="text-center py-3">No transactions found.</div>
              </template>

              <template #cell(index)="data">
                {{ data.index + 1 + offset }}.
              </template>

              <template #cell(purchase_order_id)="data">
                <div>{{ data.item.purchase_order_id || '-' }}</div>
                <small class="text-muted" v-if="data.item.purchase_order_name">
                  {{ data.item.purchase_order_name }}
                </small>
              </template>

              <template #cell(transaction_id)="data">
                {{ data.item.transaction_id || data.item.pidx || '-' }}
              </template>

              <template #cell(created_at)="data">
                {{ formatDate(data.item.created_at) }}
              </template>

              <template #cell(status_label)="data">
                <span class="status-pill" :class="statusClass(data.item)">
                  {{ data.item.status_label || data.item.status || '-' }}
                </span>
              </template>

              <template #cell(amount)="data">
                {{ formatAmount(data.item) }}
              </template>

              <!-- Fee amount only. The RATE is not shown: it varies per merchant and
                   per transaction, so printing one row's percentage invites it being
                   read as the account's standing rate. -->
              <template #cell(fee)="data">
                <div>{{ formatFee(data.item) || '-' }}</div>
              </template>

              <template #cell(net_amount)="data">
                <strong>{{ formatNet(data.item) || '-' }}</strong>
              </template>

              <template #cell(details)="data">
                <a href="#" class="btn btn-primary btn-sm" @click.prevent="openDetail(data.item)">Detail</a>
              </template>
            </b-table>

            <nav aria-label="Transaction pagination" v-if="totalPages > 1">
              <ul class="pagination">
                <li class="page-item" :class="currentPage <= 1 ? 'disabled' : ''">
                  <a href="#" class="page-link" @click.prevent="goToPage(currentPage - 1)">&laquo; Previous</a>
                </li>
                <li
                  class="page-item"
                  v-for="index in pageWindow"
                  :key="index"
                  :class="currentPage === index ? 'active' : ''"
                >
                  <a href="#" class="page-link" @click.prevent="goToPage(index)">{{ index }}</a>
                </li>
                <li class="page-item" :class="currentPage >= totalPages ? 'disabled' : ''">
                  <a href="#" class="page-link" @click.prevent="goToPage(currentPage + 1)">Next &raquo;</a>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>
    <footer>
      <MerchantFooter />
    </footer>

    <!-- DETAIL -->
    <b-modal id="payment-detail-modal" centered size="lg" title="Transaction Detail">
      <div v-if="detailLoading" class="text-center py-5">
        <b-spinner label="Loading..."></b-spinner>
      </div>
      <div v-else class="txn-detail">
        <!-- summary header -->
        <div class="txn-head">
          <div class="txn-amount">{{ detail.amountText }}</div>
          <span class="status-pill" :class="detail.statusClass">{{ detail.statusText }}</span>
          <div class="txn-sub" v-if="detail.orderLine">{{ detail.orderLine }}</div>
          <div class="txn-sub txn-net" v-if="detail.netLine">{{ detail.netLine }}</div>
        </div>

        <div class="txn-section" v-for="(section, si) in detailSections" :key="'s' + si">
          <h6 class="txn-section-title">{{ section.title }}</h6>
          <div class="txn-row" v-for="(f, i) in section.items" :key="'r' + si + '-' + i">
            <span class="txn-key">{{ f.label }}</span>
            <span class="txn-val" :class="f.mono ? 'is-mono' : ''">{{ f.value || '-' }}</span>
          </div>
        </div>
      </div>

      <template #modal-footer="{ cancel }">
        <b-button size="sm" variant="primary" :disabled="detailLoading || isPdfSaving" @click="downloadDetailPdf">
          <span class="material-icons align-middle pdf-icon">picture_as_pdf</span>
          {{ isPdfSaving ? "Preparing..." : "Download PDF" }}
        </b-button>
        <b-button size="sm" variant="secondary" @click="cancel()">Close</b-button>
      </template>
    </b-modal>

    <b-toast id="payment-error-toast" variant="danger" solid append-toast toaster="b-toaster-top-center">
      <div slot="toast-title" class="d-flex flex-grow-1 align-items-baseline">
        <strong class="mr-auto">Error</strong>
      </div>
      <div><p>{{ message }}</p></div>
    </b-toast>
    <b-toast id="payment-success-toast" variant="success" solid append-toast toaster="b-toaster-top-center">
      <div slot="toast-title" class="d-flex flex-grow-1 align-items-baseline">
        <strong class="mr-auto">Export</strong>
      </div>
      <div><p>Your download has started.</p></div>
    </b-toast>
  </div>
</template>

<script>
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import MerchantHeader from "./MerchantHeader.vue";
import MerchantFooter from "./MerchantFooter.vue";
import MerchantSideNav from "./MerchantSideNav.vue";
import MenuToggle from "./MenuToggle.vue";
import { mapActions, mapState } from "vuex";
import { jsPDF } from "jspdf";
import logoUrl from "@/assets/amej-pay.png";

export default {
  name: "MerchantPaymentList",
  components: { MerchantHeader, MerchantFooter, MerchantSideNav, MenuToggle, Loading },
  props: {
    title: { type: String, default: "Transactions" },
    // Status sent to the API by default for this screen
    defaultStatus: { type: String, default: "" },
    // Dropdown choices, e.g. [{ value: 'paid', text: 'Completed' }]
    statusOptions: { type: Array, default: function () { return []; } },
  },
  data() {
    return {
      isLoading: false,
      isExporting: false,
      isPdfSaving: false,
      detailLoading: false,
      perPage: 20,
      filters: {
        status: this.defaultStatus,
        search: "",
        dateFrom: "",
        dateTo: "",
      },
      tableVariants: ["light"],
      headVariant: ["light"],
      fields: [
        { key: "index", label: "S.N." },
        { key: "purchase_order_id", label: "Order", sortable: true },
        { key: "transaction_id", label: "Transaction ID", sortable: true },
        { key: "payer_email", label: "Payer", sortable: true },
        { key: "created_at", label: "Date & Time", sortable: true },
        { key: "status_label", label: "Status" },
        { key: "amount", label: "Amount", sortable: true },
        { key: "fee", label: "Fee", sortable: true },
        { key: "net_amount", label: "Net", sortable: true },
        { key: "details", label: "Details" },
      ],
    };
  },
  computed: {
    ...mapState(["merchantPayments", "merchantPaymentDetail", "message", "successmessage", "errormessage", "merchantEnvironment"]),
    payments: function () {
      const p = this.merchantPayments;
      return (p && typeof p === "object") ? p : { count: 0, limit: 20, offset: 0, results: [] };
    },
    rows: function () {
      const list = Array.isArray(this.payments.results) ? this.payments.results : [];
      const env = this.merchantEnvironment;
      if (!env) { return list; }
      // We already send ?environment=... . If the API ignores it, this keeps the
      // wrong environment's rows off the screen. When the API does filter, this
      // is a no-op because every row already matches.
      const tagged = list.filter(function (r) { return r && r.environment; });
      if (!tagged.length) { return list; }
      return list.filter(function (r) { return !r.environment || r.environment === env; });
    },
    totalItems: function () {
      return this.payments.count || 0;
    },
    offset: function () {
      return this.payments.offset || 0;
    },
    currentPage: function () {
      return Math.floor(this.offset / this.perPage) + 1;
    },
    totalPages: function () {
      return Math.max(1, Math.ceil(this.totalItems / this.perPage));
    },
    /** Totals for the rows currently rendered. Cents in, so the sums cannot drift. */
    pageTotals: function () {
      const rows = this.rows || [];
      let gross = 0, fee = 0, net = 0;
      let currency = "";
      for (let i = 0; i < rows.length; i++) {
        const r = rows[i];
        gross += this.moneyOf(r, "amount") || 0;
        fee += this.moneyOf(r, "fee") || 0;
        const n = this.moneyOf(r, "net_amount");
        net += (n === null) ? ((this.moneyOf(r, "amount") || 0) - (this.moneyOf(r, "fee") || 0)) : n;
        if (!currency && r && r.currency) { currency = r.currency; }
      }
      return {
        gross: this.money(gross, currency),
        fee: this.money(fee, currency),
        net: this.money(net, currency),
      };
    },
    firstIndex: function () {
      return this.totalItems ? this.offset + 1 : 0;
    },
    lastIndex: function () {
      return Math.min(this.offset + this.rows.length, this.totalItems);
    },
    // At most 5 page links around the current page
    pageWindow: function () {
      const total = this.totalPages;
      const cur = this.currentPage;
      let start = Math.max(1, cur - 2);
      let end = Math.min(total, start + 4);
      start = Math.max(1, end - 4);
      const out = [];
      for (let i = start; i <= end; i++) { out.push(i); }
      return out;
    },
    detail: function () {
      const d = this.merchantPaymentDetail || {};
      const order = [d.purchase_order_id, d.purchase_order_name].filter(function (x) { return x; });
      const fee = this.moneyOf(d, "fee");
      return {
        amountText: this.formatAmount(d) || "-",
        statusText: d.status_label || d.status || "-",
        statusClass: this.statusClass(d),
        orderLine: order.join("  \u00b7  "),
        // Only worth showing the split when a fee was actually charged.
        netLine: (fee !== null && fee > 0)
          ? ("Fee " + this.formatFee(d) + "  \u00b7  Net " + this.formatNet(d))
          : "",
      };
    },
    detailSections: function () {
      const d = this.merchantPaymentDetail || {};
      if (!Object.keys(d).length) { return []; }
      return [
        {
          title: "Payment",
          items: [
            { label: "Transaction ID", value: d.transaction_id, mono: true },
            { label: "Payment ID (pidx)", value: d.pidx, mono: true },
            { label: "Status", value: d.status_label || d.status },
            { label: "Payment Method", value: d.payment_method },
          ],
        },
        {
          // The whole point of the fee feature: what was charged, what was taken,
          // and what actually lands in the merchant account.
          title: "Settlement",
          items: [
            { label: "Gross Amount", value: this.formatAmount(d) },
            { label: "Processing Fee", value: this.formatFee(d) },
            { label: "Net Amount Received", value: this.formatNet(d) },
            { label: "Currency", value: d.currency },
          ],
        },
        {
          title: "Order",
          items: [
            { label: "Order ID", value: d.purchase_order_id },
            { label: "Order Name", value: d.purchase_order_name },
            { label: "Payer Email", value: d.payer_email },
          ],
        },
        {
          title: "Timeline",
          items: [
            { label: "Created At", value: this.formatDate(d.created_at) },
            { label: "Paid At", value: this.formatDate(d.paid_at) },
          ],
        },
        {
          title: "Other",
          items: [
            { label: "Remarks", value: d.remarks },
          ],
        },
      ];
    },
  },
  created() {
    this.syncFromRoute();
    this.fetchList();
  },
  watch: {
    "$route.query": function () {
      this.syncFromRoute();
      this.fetchList();
    },
    // Live <-> Sandbox switch in the header reloads the list
    merchantEnvironment: function () {
      this.fetchList();
    },
  },
  methods: {
    ...mapActions(["listMerchantPayments", "getMerchantPaymentByPidx", "exportMerchantPayments"]),

    syncFromRoute() {
      const q = this.$route.query || {};
      // `all=1` marks an explicit "All Status" choice so it survives a refresh
      if (q.all === "1") { this.filters.status = ""; }
      else if (q.status !== undefined) { this.filters.status = q.status; }
      if (q.search !== undefined) { this.filters.search = q.search; }
      if (q.date_from !== undefined) { this.filters.dateFrom = q.date_from; }
      if (q.date_to !== undefined) { this.filters.dateTo = q.date_to; }
    },

    currentQuery() {
      const page = parseInt(this.$route.query.page || 1, 10) || 1;
      // filters.status === "" means the user picked "All Status" -> send no status param
      return {
        status: this.filters.status,
        search: this.filters.search,
        dateFrom: this.filters.dateFrom,
        dateTo: this.filters.dateTo,
        environment: this.merchantEnvironment,
        limit: this.perPage,
        offset: (page - 1) * this.perPage,
      };
    },

    fetchList() {
      this.isLoading = true;
      const vm = this;
      return this.listMerchantPayments(this.currentQuery())
        .then(function () {
          if (vm.errormessage === "errormessage") {
            vm.$bvToast.show("payment-error-toast");
          }
        })
        .then(function () { vm.isLoading = false; })
        .catch(function () { vm.isLoading = false; });
    },

    pushQuery(extra) {
      const q = Object.assign({}, this.$route.query, extra);
      Object.keys(q).forEach(function (k) {
        if (q[k] === "" || q[k] === null || q[k] === undefined) { delete q[k]; }
      });
      // avoid NavigationDuplicated
      if (JSON.stringify(q) === JSON.stringify(this.$route.query)) {
        this.fetchList();
        return;
      }
      this.$router.push({ query: q }).catch(function () {});
    },

    applyFilters() {
      this.pushQuery({
        page: 1,
        all: this.filters.status === "" ? "1" : undefined,
        status: this.filters.status || undefined,
        search: this.filters.search || undefined,
        date_from: this.filters.dateFrom || undefined,
        date_to: this.filters.dateTo || undefined,
      });
    },

    resetFilters() {
      this.filters.status = this.defaultStatus;
      this.filters.search = "";
      this.filters.dateFrom = "";
      this.filters.dateTo = "";
      this.pushQuery({ page: 1, all: undefined, status: undefined, search: undefined, date_from: undefined, date_to: undefined });
    },

    goToPage(page) {
      if (page < 1 || page > this.totalPages || page === this.currentPage) { return; }
      this.pushQuery({ page: page });
      window.scrollTo(0, 0);
    },

    openDetail(item) {
      const pidx = item.pidx || item.transaction_id;
      if (!pidx) { return; }
      this.detailLoading = true;
      this.$bvModal.show("payment-detail-modal");
      const vm = this;
      return this.getMerchantPaymentByPidx(pidx)
        .then(function () { vm.detailLoading = false; })
        .catch(function () { vm.detailLoading = false; });
    },

    exportList() {
      if (this.isExporting) { return; }
      this.isExporting = true;
      const vm = this;
      const params = this.currentQuery();
      delete params.limit;
      delete params.offset;
      return this.exportMerchantPayments(params)
        .then(function () {
          if (vm.successmessage === "success") {
            vm.$bvToast.show("payment-success-toast");
          } else {
            vm.$bvToast.show("payment-error-toast");
          }
        })
        .then(function () { vm.isExporting = false; })
        .catch(function () { vm.isExporting = false; });
    },

    /** Loads the aPay logo as an <img> so jsPDF can embed it. Resolves null on failure. */
    loadLogo() {
      return new Promise(function (resolve) {
        try {
          const img = new Image();
          img.onload = function () { resolve(img); };
          img.onerror = function () { resolve(null); };
          img.src = logoUrl;
        } catch (e) {
          resolve(null);
        }
      });
    },

    /** Single-transaction PDF built with jsPDF (already a project dependency). */
    downloadDetailPdf() {
      const d = this.merchantPaymentDetail || {};
      if (!Object.keys(d).length) { return; }
      this.isPdfSaving = true;
      const vm = this;
      return this.loadLogo().then(function (logo) {
        vm.buildDetailPdf(d, logo);
        vm.isPdfSaving = false;
      }).catch(function () {
        vm.isPdfSaving = false;
        vm.$bvToast.show("payment-error-toast");
      });
    },

    buildDetailPdf(d, logo) {
      try {
        const doc = new jsPDF("p", "pt", "a4");
        const pageW = doc.internal.pageSize.getWidth();
        const left = 48;
        const right = pageW - 48;
        let y = 60;

        // aPay logo (natural ratio 198x106)
        if (logo) {
          try {
            const logoW = 74;
            const logoH = Math.round(logoW * (106 / 198));
            doc.addImage(logo, "PNG", left, 36, logoW, logoH);
          } catch (e) {
            // embedding failed - carry on without the logo
          }
        }

        // Title (right of the logo)
        doc.setFontSize(18);
        doc.setTextColor(20, 20, 20);
        doc.text("Transaction Detail", right, y, { align: "right" });

        // Amount + status (clear of the logo block above)
        y += 52;
        doc.setFontSize(22);
        doc.setTextColor(0, 102, 179);
        doc.text(String(this.detail.amountText), left, y);
        doc.setFontSize(11);
        doc.setTextColor(90, 90, 90);
        doc.text(String(this.detail.statusText), right, y, { align: "right" });

        // Divider
        y += 14;
        doc.setDrawColor(220, 220, 220);
        doc.line(left, y, right, y);

        const sections = this.detailSections;
        for (let s = 0; s < sections.length; s++) {
          const section = sections[s];
          const items = section.items.filter(function (i) { return i.value; });
          if (!items.length) { continue; }

          y += 30;
          if (y > 760) { doc.addPage(); y = 60; }
          doc.setFontSize(12);
          doc.setTextColor(20, 20, 20);
          doc.text(section.title, left, y);
          y += 8;
          doc.setDrawColor(235, 235, 235);
          doc.line(left, y, right, y);

          for (let i = 0; i < items.length; i++) {
            y += 22;
            if (y > 780) { doc.addPage(); y = 60; }
            doc.setFontSize(10);
            doc.setTextColor(110, 110, 110);
            doc.text(String(items[i].label), left, y);
            doc.setTextColor(25, 25, 25);
            const value = String(items[i].value);
            const lines = doc.splitTextToSize(value, 300);
            doc.text(lines, right, y, { align: "right" });
            if (lines.length > 1) { y += (lines.length - 1) * 12; }
          }
        }

        // Footer
        y = Math.min(y + 40, 800);
        doc.setFontSize(9);
        doc.setTextColor(150, 150, 150);
        doc.text("Generated on " + this.formatDate(new Date().toISOString()) + " - Amej Pay", left, y);

        const name = d.transaction_id || d.pidx || "transaction";
        doc.save("transaction-" + String(name).replace(/[^A-Za-z0-9_-]/g, "") + ".pdf");
      } catch (e) {
        this.$bvToast.show("payment-error-toast");
      }
    },

    formatDate(value) {
      if (!value) { return ""; }
      const d = new Date(value);
      if (isNaN(d.getTime())) { return String(value); }
      return d.toLocaleString("en-GB", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    },

    /**
     * Reads one money field, preferring the *_cents integer.
     *
     * The API sends both `amount: 150.0` and `amount_cents: 15000`. The integer is the
     * source of truth - binary floats cannot represent every 2-decimal value exactly, and
     * on a fee of a few percent that drift is what makes gross - fee != net by a cent.
     * Returns null when the field is genuinely absent, so "0.00" and "not provided" stay
     * distinguishable.
     */
    moneyOf(item, key) {
      if (!item) { return null; }
      const cents = item[key + "_cents"];
      if (cents !== undefined && cents !== null && cents !== "") {
        const c = Number(cents);
        if (!isNaN(c)) { return c / 100; }
      }
      const raw = item[key];
      if (raw === undefined || raw === null || raw === "") { return null; }
      const n = Number(raw);
      return isNaN(n) ? null : n;
    },

    money(value, currency) {
      if (value === null || value === undefined) { return ""; }
      return (currency ? currency + " " : "") + Number(value).toFixed(2);
    },

    /** Gross amount the payer was charged. */
    formatAmount(item) {
      if (!item) { return ""; }
      return this.money(this.moneyOf(item, "amount"), item.currency);
    },

    /** Per-transaction processing fee. */
    formatFee(item) {
      if (!item) { return ""; }
      const fee = this.moneyOf(item, "fee");
      if (fee === null) { return ""; }
      return this.money(fee, item.currency);
    },

    /**
     * What actually reaches the merchant account.
     * Falls back to gross - fee when the API omits net_amount, so an older payload
     * still shows something correct rather than a blank column.
     */
    formatNet(item) {
      if (!item) { return ""; }
      let net = this.moneyOf(item, "net_amount");
      if (net === null) {
        const gross = this.moneyOf(item, "amount");
        const fee = this.moneyOf(item, "fee");
        if (gross === null) { return ""; }
        net = gross - (fee || 0);
      }
      return this.money(net, item.currency);
    },

    // feePercentText was removed: the fee RATE is no longer displayed anywhere on the
    // merchant side. It differs per merchant and per transaction, so showing one row's
    // percentage reads as the account's standing rate. The fee AMOUNT is still shown,
    // and `fee_percent` remains in the payload for anyone who needs it.

    statusClass(item) {
      const s = String(item.status || "").toLowerCase();
      if (["paid", "completed", "success", "successful"].indexOf(s) !== -1) { return "is-success"; }
      if (["pending", "initiated", "processing"].indexOf(s) !== -1) { return "is-pending"; }
      return "is-failed";
    },
  },
};
</script>

<style scoped>
.txn-detail { padding: 4px 2px; }
.txn-net { color: #0066b3; font-weight: 600; }
.page-totals { color: #6b7280; }
.page-totals .pt-label { margin-right: 10px; text-transform: uppercase; letter-spacing: .4px; font-size: 11px; }
.page-totals .pt-item { margin-right: 16px; }
.page-totals .pt-item strong { color: #2c3e50; }
.txn-head {
  border: 1px solid #e9ecef;
  background: #f8f9fb;
  border-radius: 8px;
  padding: 16px 18px;
  margin-bottom: 18px;
}
.txn-amount {
  font-size: 26px;
  font-weight: 700;
  color: #0066b3;
  line-height: 1.2;
  display: inline-block;
  margin-right: 12px;
}
.txn-sub {
  margin-top: 6px;
  font-size: 13px;
  color: #6c757d;
}
.txn-section { margin-bottom: 18px; }
.txn-section-title {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #6c757d;
  margin: 0 0 6px 0;
  padding-bottom: 6px;
  border-bottom: 1px solid #eef0f2;
}
.txn-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 9px 2px;
  border-bottom: 1px solid #f4f5f7;
  gap: 16px;
}
.txn-row:last-child { border-bottom: none; }
.txn-key {
  color: #6c757d;
  font-size: 14px;
  flex: 0 0 auto;
}
.txn-val {
  color: #1b1b1b;
  font-size: 14px;
  font-weight: 600;
  text-align: right;
  word-break: break-word;
}
.txn-val.is-mono {
  font-family: "Courier New", Courier, monospace;
  font-weight: 500;
}
.pdf-icon { font-size: 17px; margin-right: 4px; }
.status-pill {
  font-size: 12px;
  padding: 3px 10px;
  border-radius: 10px;
  font-weight: 600;
  text-transform: capitalize;
  white-space: nowrap;
}
.status-pill.is-success { background: #e3f7e3; color: #00a800; }
.status-pill.is-pending { background: #fdf3e0; color: #e08b00; }
.status-pill.is-failed  { background: #fde3e5; color: #dc3545; }
.download-link {
  display: inline-flex;
  align-items: center;
  color: #0066b3;
  font-weight: 600;
  text-decoration: none;
}
.download-link .material-icons { margin-right: 6px; }
.text-muted { color: #6c757d; font-size: 12px; }
.pagination .page-item.disabled .page-link { pointer-events: none; opacity: 0.5; }
</style>
